import Pptxgen from 'pptxgenjs';
import { DateObject } from 'react-multi-date-picker';
import { User } from '@auth0/auth0-spa-js';

import {
  GRAPH_PERIOD_TYPE,
  GRAPH_WASTE_TYPE,
  GRAPH_WITH_WITHOUT_PRODUCTION,
  GRAPH_WIDTH_TYPE,
  SLIDE_ATTRIBUTE_BY_HEADER_TEXT,
  SLIDE_CONTENT_TYPE,
  SLIDE_STYLE,
  SLIDE_TYPE,
  WEIGHT_COST_TYPE,
} from '../../constant/LocationMonthlyReviewPowerpoint';
import {
  insertGraph,
  insertImage,
  insertIssueImageSlideForAllServices,
  insertServiceTop6MenuItemTable,
  insertStationGraph,
  insertStationImage,
  insertTable,
  initialiseSlide,
} from '../../functions/PowerpointFunctions';
import {
  GraphPlotData,
  LocationMonthlyReportData,
  LocationService,
  Service,
  StationGraphData,
} from '../../interfaces';
import { SlideContent } from './EditMonthlyReviewSlide/helper';

const MAX_NUMBER_OF_BARS_GENERAL = 9;
const MAX_NUMBER_OF_BARS_FOR_STATION = 3;
const NUMBER_OF_IMAGES_DISPLAYED_FOR_PLATE_WASTE_SERVICE = 12;
const NUMBER_OF_IMAGES_DISPLAYED_FOR_NON_PLATE_WASTE_SERVICE = 4;
const INVALID_WASTE_PER_COVER_VALUE = 0.000001;

// Currently not used as the line axis label values are hidden. But keeping it in case it is required again.
// This function sets the number that is 100 and above to nearest upper 100, and the number below 100 to nearest upper 10.
// const roundToNearest100 = (num: number) => {
//   if (num < 100) {
//     return Math.ceil(num / 10) * 10;
//   }
//   return Math.ceil(num / 100) * 100;
// };

/**
 * Return 1 as the maximum y-axis if the maximum y value is INVALID_WASTE_PER_COVER_VALUE so that for the INVALID_WASTE_PER_COVER_VALUE value, there will be at least
 * a scale of 0 - 1 for the y-axis. Else
 * - for bar: return the max value as the actual value multiplied by a factor
 * - for line: return the max value as the actual value multiplied by a factor and the min value as the max value mutipled by a negative factor
 * This is to ensure that in the mixed graph, the line and bar graphs are separated such that the line is positioned entirely above the bar graph without any overlapping.
 * @param arrWasteValue - Array of waste values to find if there is a maximum y-axis value to be set
 * @param isForBarAxis - Indicates if the y-axis plot values ares to be found for the bar or line
 * @param isMixedGraph - Indicates if the type of graph that y-axis plot values is mixed graph or pure bar graph
 * @returns maxValue - Maximum value of the y-axis. Undefined if the max value is not to be set.
 */
const getMinMaxActualYAxisPlotValues = (
  arrWasteValue: (number | null)[],
  isForBarAxis: boolean,
  isMixedGraph?: boolean
): { maxValue: number; minValue: number } => {
  const arrWasteValueWithNull = arrWasteValue.map((wasteValue) => {
    return wasteValue === null ? 0 : wasteValue;
  });
  let maxValue = Math.max(...arrWasteValueWithNull);
  const minMaxActualYAxisPlotValues = {
    maxValue: 0,
    minValue: 0,
    // actualValue: maxValue, // Currently not used as the line axis label values are hidden
    // step: 1, // For line graph only // Currently not used as the line axis label values are hidden
  };

  if (
    maxValue === 0 ||
    maxValue === INVALID_WASTE_PER_COVER_VALUE ||
    arrWasteValueWithNull.length === 0
  ) {
    minMaxActualYAxisPlotValues.maxValue = 1;
  } else if (isForBarAxis) {
    minMaxActualYAxisPlotValues.maxValue = maxValue * (isMixedGraph ? 1 : 1.85);
  } else {
    minMaxActualYAxisPlotValues.maxValue = maxValue * 1.28;
    minMaxActualYAxisPlotValues.minValue = maxValue * -2.75;
    // Currently not used as the line axis label values are hidden. But keeping it in case it is required again.
    // This sets the line axis labels as nice numbers where 0 is always displayed. This is important as the negative values are supposed to
    // be hidden (a hacky way to separate the line and stacked bar graph) and the axis should look like it starts from 0.
    // minMaxActualYAxisPlotValues.maxValue = roundToNearest100(maxValue * 1.1);
    // const factor = minMaxActualYAxisPlotValues.maxValue <= 100 ? 5 : 4;
    // minMaxActualYAxisPlotValues.step = Math.floor(minMaxActualYAxisPlotValues.maxValue / factor);
    // minMaxActualYAxisPlotValues.minValue =
    //   minMaxActualYAxisPlotValues.step * -Math.ceil(factor * 2.38);
  }
  return minMaxActualYAxisPlotValues;
};

/**
 * This function takes arrData, and pads them with invisible bar columns / line points to allow them to be centralised among the
 * white space within the chart.
 * 1. For charts with baseline shown (i.e. string 'Baseline' as the first element in arrXAxisValue), the baseline
 * will always be the first bar and line shown, followed by the remaining bars of fixed widths centered among the remaining
 * space up to a maximum of x bars shown in total (as per baseline 8-week chart logic).
 * 2. For bar charts without baseline shown, the bars are centered at fixed widths among the entire chart width if there
 * are less than x bars present. If there are more than x bars present (i.e. more than x months' date range chosen), each
 * bar is subsequently narrowed to fit within the available white space.
 * @param arrData - Array of data that is structured in the format required by the pptxgen addChart
 * @param maxNumberOfBars - Maximum number of bars for different padding
 * @returns arrData - Array of data that has accounted for padding
 */
const padLabelsAndValuesToCentraliseChart = (
  arrData: { name: string; labels: string[]; values: any[] }[],
  maxNumberOfBars: number
) => {
  return arrData.map(({ name, labels, values }) => {
    const hasBaseline = labels[0] === 'Baseline';
    const numberOfBars = values.length;
    let barsToAdd = 0;
    if (numberOfBars < maxNumberOfBars) {
      const isEvenNumberOfBars = !Boolean(numberOfBars % 2);
      barsToAdd = maxNumberOfBars - numberOfBars + (isEvenNumberOfBars ? 1 : 0);
    }
    const valuesWithNullBars = hasBaseline
      ? [values[0]].concat(
          Array(barsToAdd / 2).fill(null),
          values.slice(1),
          Array(barsToAdd / 2).fill(null)
        )
      : Array(barsToAdd / 2)
          .fill(null)
          .concat(values, Array(barsToAdd / 2).fill(null));
    const labelsWithEmptyBars = hasBaseline
      ? [labels[0]].concat(
          Array(barsToAdd / 2).fill(''),
          labels.slice(1),
          Array(barsToAdd / 2).fill('')
        )
      : Array(barsToAdd / 2)
          .fill('')
          .concat(labels, Array(barsToAdd / 2).fill(''));
    return {
      name,
      values: valuesWithNullBars,
      labels: labelsWithEmptyBars,
    };
  });
};

/**
 * Retrieve the required data from graphData and structure it in the format required by pptxgen addChart for location/service group
 * bar and/or line graph
 * @param graphData - Graph data to be structured in the format required by pptxgen addChart
 * @param graphDataAttributes - Attributes that determine what values to retrieve from graphData
 * @param currency - Currency
 * @returns graphPlotData
 */
const retrieveBarOrLineGraphPlotData = (
  graphData: LocationMonthlyReportData['graph']['location'],
  graphDataAttributes: {
    periodType: keyof typeof GRAPH_PERIOD_TYPE;
    barType: 'weight' | 'cost' | 'weightPerCover';
    lineType: 'cover' | 'throw' | undefined;
    withWithoutProduction: keyof typeof GRAPH_WITH_WITHOUT_PRODUCTION;
    widthType: keyof typeof GRAPH_WIDTH_TYPE;
    includeBreakdown?: boolean;
    isDisplayBarLegend: boolean;
  },
  currency: string
): GraphPlotData => {
  const {
    periodType,
    barType,
    lineType,
    withWithoutProduction,
    widthType,
    includeBreakdown,
    isDisplayBarLegend,
  } = graphDataAttributes;
  const graphDataOfAPeriodSelected = graphData[withWithoutProduction][periodType];
  const { arrService: arrServiceGraphData } = graphData;
  const hasLineGraph = !!lineType;
  let barGraphData;
  let barGraphDataForSummedValues;
  let barMinMaxActualYAxisPlotValues;
  if (includeBreakdown) {
    barGraphData = arrServiceGraphData.map((serviceGraphData) => {
      const serviceGraphDataRequired = serviceGraphData[withWithoutProduction][periodType];
      return {
        name: serviceGraphData.name,
        labels: serviceGraphDataRequired.date,
        values: serviceGraphDataRequired[barType as keyof typeof serviceGraphDataRequired],
      };
    });
    barGraphDataForSummedValues = [
      {
        name: '',
        labels: graphDataOfAPeriodSelected.date,
        values: graphDataOfAPeriodSelected[barType as keyof typeof graphDataOfAPeriodSelected],
      },
    ];
  } else {
    barGraphData = [
      {
        name: '',
        labels: graphDataOfAPeriodSelected.date,
        values: graphDataOfAPeriodSelected[barType as keyof typeof graphDataOfAPeriodSelected],
      },
    ];
    barGraphDataForSummedValues = [
      {
        name: '',
        labels: graphDataOfAPeriodSelected.date,
        values: graphDataOfAPeriodSelected[barType as keyof typeof graphDataOfAPeriodSelected],
      },
    ];
  }
  barMinMaxActualYAxisPlotValues = getMinMaxActualYAxisPlotValues(
    barGraphDataForSummedValues[0].values as (number | null)[],
    true,
    !hasLineGraph
  );
  barGraphData = padLabelsAndValuesToCentraliseChart(barGraphData, MAX_NUMBER_OF_BARS_GENERAL);
  barGraphDataForSummedValues = padLabelsAndValuesToCentraliseChart(
    barGraphDataForSummedValues,
    MAX_NUMBER_OF_BARS_GENERAL
  );

  let lineGraphData;
  let lineMinMaxActualYAxisPlotValues;
  if (hasLineGraph) {
    const graphDataRequired = graphData[withWithoutProduction][periodType];
    lineGraphData = [
      {
        name: lineType === GRAPH_WASTE_TYPE.throw ? 'Total Throws' : 'Total Cover',
        labels: graphDataRequired.date,
        values: graphDataRequired[lineType as keyof typeof graphDataRequired],
      },
    ];
    lineGraphData = padLabelsAndValuesToCentraliseChart(lineGraphData, MAX_NUMBER_OF_BARS_GENERAL);
    lineMinMaxActualYAxisPlotValues = getMinMaxActualYAxisPlotValues(
      lineGraphData[0].values as (number | null)[],
      false
    );
  }

  const graphPlotData = {
    bar: barGraphData,
    line: lineGraphData,
    barGraphDataForSummedValues,
    barMinMaxActualYAxisPlotValues,
    lineMinMaxActualYAxisPlotValues,
    barType,
    lineType,
    widthType,
    currency,
    isDisplayBarLegend,
  };
  return graphPlotData;
};

/**
 * Retrieve the required data from graphData and structure it in the format required by pptxgen addChart for station bar graph
 * @param graphData - Graph data to be structured in the format required by pptxgen addChart
 * @param graphDataAttributes - Attributes that determine what values to retrieve from graphData
 * @param currency - Currency
 * @returns graphPlotData
 */
const retrieveStationBarGraphPlotData = (
  graphData: StationGraphData,
  graphDataAttributes: {
    barType: 'weight' | 'cost';
    widthType: keyof typeof GRAPH_WIDTH_TYPE;
    isDisplayBarLegend: boolean;
  },
  serviceName: string,
  currency: string
): GraphPlotData => {
  const { barType, widthType, isDisplayBarLegend } = graphDataAttributes;
  const graphPlotData = {
    bar: padLabelsAndValuesToCentraliseChart(
      [
        {
          name: serviceName,
          labels: graphData.station,
          values: graphData[barType as keyof typeof graphData],
        },
      ],
      MAX_NUMBER_OF_BARS_FOR_STATION
    ),
    barGraphDataForSummedValues: padLabelsAndValuesToCentraliseChart(
      [
        {
          name: serviceName,
          labels: graphData.station,
          values: graphData[barType as keyof typeof graphData],
        },
      ],
      MAX_NUMBER_OF_BARS_FOR_STATION
    ),
    barType,
    widthType,
    currency,
    isDisplayBarLegend,
  };
  return graphPlotData;
};

/**
 * This function holds the logic required to extract the locationData provided by the backend and plots them within
 * a predetermined slide order template structure
 * Within the process, there is a check to stop the function when isPropertyMonthlyReviewCancelledRef.current is false.
 * @param fetchGraphRes - Response from backend containing information required to be plot into the powerpoint slides
 * @param monthlyReviewSlideMetadata - Response from backend containing information required to be plot into the powerpoint slides
 * @param mapLocationNameByLocationId - Map of key: locationId and value: locationName
 * @param mapServiceNameByServiceId - Map of key: serviceId and value: serviceName
 * @param formState - Report form header values used for powerpoint slide headers
 * @param user - User provided by Auth0 SDK to place the user's email address in the closing slide
 * @param slideImagesInBase64DataUrl - Object containing base64 data url of slide images for property monthly review
 * @param isPropertyMonthlyReviewCancelledRef - React reference that stores the boolean of whether the download is to be cancelled
 */
const downloadPropertyMonthlyReviewPowerpoint = async (
  fetchGraphRes: any,
  monthlyReviewSlideMetadata: Array<SlideContent>,
  mapLocationNameByLocationId: Map<number, string>,
  mapServiceNameByServiceId: Map<number, string>,
  formState: {
    companyField: string;
    restaurantField: string;
    mapSetSelectedServiceIdBySelectedLocationId: Map<number, Set<number>>;
    dateRange: Array<DateObject>;
    isTagged: boolean | string | undefined;
    includeIssueSlides: boolean;
  },
  user: User | undefined,
  slideImagesInBase64DataUrl: { [key: string]: string },
  isPropertyMonthlyReviewCancelledRef: React.MutableRefObject<boolean>
) => {
  const { arrLocationData, restaurantService, fileName } = fetchGraphRes;
  const { setLocationIdToGenerate, setServiceIdToGenerate, setGroupNameToGenerate } =
    restaurantService.arrLocationService.reduce(
      (
        {
          setLocationIdToGenerate,
          setServiceIdToGenerate,
          setGroupNameToGenerate,
        }: {
          setLocationIdToGenerate: Set<number>;
          setServiceIdToGenerate: Set<number>;
          setGroupNameToGenerate: Set<string>;
        },
        locationService: LocationService
      ) => {
        const { locationId, arrService } = locationService;
        setLocationIdToGenerate.add(locationId);
        arrService.forEach((service: Service) => {
          const { serviceId, group } = service;
          setServiceIdToGenerate.add(serviceId);
          if (group) setGroupNameToGenerate.add(group);
        });
        return { setLocationIdToGenerate, setServiceIdToGenerate, setGroupNameToGenerate };
      },
      {
        setLocationIdToGenerate: new Set(),
        setServiceIdToGenerate: new Set(),
        setGroupNameToGenerate: new Set(),
      }
    );
  const { dateRange, includeIssueSlides } = formState;
  // Map initialised for easier retrival during powerpoint generation
  const mapLocationDataByLocationId = new Map<number, LocationMonthlyReportData>(
    arrLocationData.map((locationData: LocationMonthlyReportData) => [
      locationData.locationId,
      locationData,
    ])
  );
  const restaurantName = restaurantService.name;
  const currency = restaurantService.arrLocationService[0].arrService[0].currency;
  // Powerpoint creation
  const pres = new Pptxgen();
  if (isPropertyMonthlyReviewCancelledRef.current) return;
  // Append starting slide
  const openingText = `${restaurantName}\nMonthly Review (${dateRange[1].format('MMMM-YYYY')})`;
  initialiseSlide(pres, SLIDE_STYLE.opening, {
    slideImagesInBase64DataUrl,
    text: openingText,
  });

  // Append main body
  for (let slideIndex = 0; slideIndex < monthlyReviewSlideMetadata.length; slideIndex += 1) {
    if (isPropertyMonthlyReviewCancelledRef.current) return;
    const { headerText, locationId, serviceId, groupName, isHidden } =
      monthlyReviewSlideMetadata[slideIndex];
    if (
      (locationId !== null && !setLocationIdToGenerate.has(locationId)) ||
      (serviceId !== -1 && serviceId !== null && !setServiceIdToGenerate.has(serviceId)) ||
      (groupName !== null && !setGroupNameToGenerate.has(groupName))
    )
      continue;
    const locationName = mapLocationNameByLocationId.get(locationId!)!;
    const serviceName = serviceId === null ? '' : mapServiceNameByServiceId.get(serviceId)!;

    const {
      graph: graphData,
      table: tableData,
      service: serviceData,
    } = mapLocationDataByLocationId.get(locationId!)!;
    const locationNameUpperCased = locationName.toUpperCase();
    const isNoGroup = tableData.byWeight.individual.length === tableData.byWeight.group.length;

    const {
      slideStyle,
      slideType,
      isExcludeForProduction,
      slideContentType,
      grouped,
      weightCostType,
      graphDataAttributes,
    } = SLIDE_ATTRIBUTE_BY_HEADER_TEXT[headerText];

    if (slideStyle === SLIDE_STYLE.section) {
      const sectionText = `${locationName}\n${headerText}`;
      initialiseSlide(pres, slideStyle, {
        slideImagesInBase64DataUrl,
        text: sectionText,
        isHidden,
      });
    } else if (
      slideStyle === SLIDE_STYLE.subSection &&
      (slideContentType === SLIDE_CONTENT_TYPE.summaryTable || includeIssueSlides)
    ) {
      initialiseSlide(pres, slideStyle, {
        slideImagesInBase64DataUrl,
        text: headerText,
        locationName: locationNameUpperCased,
        isHidden,
      });
    } else if (slideStyle === SLIDE_STYLE.plain) {
      if (slideType === SLIDE_TYPE.graph) {
        // For location level graphs
        if (serviceId === -1) {
          let slide = initialiseSlide(pres, slideStyle, {
            slideImagesInBase64DataUrl,
            text: headerText,
            locationName: locationNameUpperCased,
            isHidden,
          });
          const graphPlotData = retrieveBarOrLineGraphPlotData(
            graphData.location,
            graphDataAttributes,
            currency
          );
          insertGraph(pres, slide, graphPlotData, headerText!);
        } else {
          const serviceGroupName = groupName || serviceName;
          const matchedServiceGroupGraphData = graphData.serviceGroup[serviceGroupName];
          if (!matchedServiceGroupGraphData) continue; // If the group that was initially sorted and saved no longer exists
          const { isOnlyProduction, wastePerCoverSummary } = matchedServiceGroupGraphData;
          // If service is production, do not generate cover related slides
          // Note: Currently, weight and throw mixed graph is also not created for production service as the backend code does not generate daily graphs
          // for production service. Can be considered in future.
          if (isOnlyProduction && isExcludeForProduction) {
            continue;
          }
          const graphPlotData = retrieveBarOrLineGraphPlotData(
            matchedServiceGroupGraphData,
            graphDataAttributes,
            currency
          );
          let slide = initialiseSlide(pres, slideStyle, {
            slideImagesInBase64DataUrl,
            text: `${serviceGroupName.toUpperCase()} - ${headerText}`,
            locationName: locationNameUpperCased,
            isHidden,
          });
          // SLIDE TYPE: GRAPH
          insertGraph(pres, slide, graphPlotData, headerText, wastePerCoverSummary);
        }
      } else if (
        slideType === SLIDE_TYPE.table &&
        slideContentType === SLIDE_CONTENT_TYPE.summaryTable
      ) {
        if (grouped && isNoGroup) {
          continue;
        }
        let slide = initialiseSlide(pres, slideStyle, {
          slideImagesInBase64DataUrl,
          text: headerText,
          locationName: locationNameUpperCased,
          isHidden,
        });
        insertTable(
          pres,
          slide,
          slideImagesInBase64DataUrl,
          headerText,
          {
            currency,
            arrReductionInWeightSummary: grouped
              ? tableData.byWeight.group
              : tableData.byWeight.individual,
            arrWastePerCoverSummary: grouped
              ? tableData.byWeightPerCover.group
              : tableData.byWeightPerCover.individual,
            arrReductionInCostSummary: grouped
              ? tableData.byCost.group
              : tableData.byCost.individual,
          },
          isHidden
        );
      } else if (slideContentType === SLIDE_CONTENT_TYPE.stationAndMenuItem) {
        const {
          name,
          type,
          topStation: topStationData,
          topMenuItem: topMenuItemData,
        } = serviceData[serviceId!];
        // If service is plate waste, do not generate station slides
        if (
          type === 'Plate Waste' &&
          (headerText === 'TOP 3 LEFTOVER IN TERMS OF WEIGHT (BY STATIONS)' ||
            headerText === 'TOP 3 LEFTOVER IN TERMS OF COST (BY STATIONS)')
        ) {
          continue;
        }
        let slide = initialiseSlide(pres, slideStyle, {
          slideImagesInBase64DataUrl,
          text: `${name.toUpperCase()} - ${headerText}`,
          locationName: locationNameUpperCased,
          isHidden,
        });
        // SLIDE TYPE: IMAGE
        if (slideType === SLIDE_TYPE.image) {
          const numberOfImagesToDisplay =
            type === 'Plate Waste'
              ? NUMBER_OF_IMAGES_DISPLAYED_FOR_PLATE_WASTE_SERVICE
              : NUMBER_OF_IMAGES_DISPLAYED_FOR_NON_PLATE_WASTE_SERVICE;
          const isWeight = weightCostType === WEIGHT_COST_TYPE.weight;
          await insertImage(
            pres,
            slide,
            slideImagesInBase64DataUrl,
            isWeight ? topMenuItemData.byWeight.finalMonth : topMenuItemData.byCost.finalMonth,
            `${name.toUpperCase()} - ${headerText}`,
            locationNameUpperCased,
            currency,
            numberOfImagesToDisplay,
            isWeight,
            isHidden
          );
        } // SLIDE TYPE: STATION
        else if (slideType === SLIDE_TYPE.station) {
          if (type !== 'Plate Waste') {
            if (weightCostType === WEIGHT_COST_TYPE.weight) {
              // Only top 3 station is displayed
              const graphPlotData = retrieveStationBarGraphPlotData(
                topStationData.byWeight.graph!,
                graphDataAttributes,
                name,
                currency
              );
              insertStationGraph(pres, slide, graphPlotData);
              await insertStationImage(slide, topStationData.byWeight.value);
            } else if (weightCostType === WEIGHT_COST_TYPE.cost) {
              // Only top 3 station is displayed
              const graphPlotData = retrieveStationBarGraphPlotData(
                topStationData.byCost.graph!,
                graphDataAttributes,
                name,
                currency
              );
              insertStationGraph(pres, slide, graphPlotData);
              await insertStationImage(slide, topStationData.byCost.value, currency);
            }
          }
        }
        // SLIDE TYPE: TABLE
        else if (slideType === SLIDE_TYPE.table) {
          if (weightCostType === WEIGHT_COST_TYPE.weight) {
            insertServiceTop6MenuItemTable(
              slide,
              'Top 6 High Volume Items',
              currency,
              topMenuItemData.byWeight.finalMonth,
              topMenuItemData.byWeight.previousMonth,
              {
                isProductionService: type === 'Production',
                isWeight: weightCostType === WEIGHT_COST_TYPE.weight,
              }
            );
          } else if (weightCostType === WEIGHT_COST_TYPE.cost) {
            insertServiceTop6MenuItemTable(
              slide,
              'Top 6 High Value Items',
              currency,
              topMenuItemData.byCost.finalMonth,
              topMenuItemData.byCost.previousMonth,
              {
                isProductionService: type === 'Production',
                isWeight: weightCostType === WEIGHT_COST_TYPE.weight,
              }
            );
          }
        }
      } else if (includeIssueSlides) {
        const matchedServiceMonthlyReportData = serviceData[serviceId!];
        let arrIssueWasteAnalysis = [];
        if (headerText === 'SINGULAR THROWS') {
          arrIssueWasteAnalysis = [
            matchedServiceMonthlyReportData.issueWasteAnalysisByWeight.notInMenu,
          ];
        } else if (headerText === 'MIXED THROWS') {
          arrIssueWasteAnalysis = [
            matchedServiceMonthlyReportData.issueWasteAnalysisByWeight.mixed,
          ];
        } else {
          arrIssueWasteAnalysis = [
            matchedServiceMonthlyReportData.issueWasteAnalysisByWeight.usageIssue,
          ];
        }
        await insertIssueImageSlideForAllServices(
          pres,
          headerText,
          locationNameUpperCased,
          arrIssueWasteAnalysis,
          true,
          isHidden
        );
      }
    }
  }
  if (isPropertyMonthlyReviewCancelledRef.current) return;
  // Append closing slide
  initialiseSlide(pres, SLIDE_STYLE.closing, {
    slideImagesInBase64DataUrl,
    text: user?.email,
  });
  if (isPropertyMonthlyReviewCancelledRef.current) return;
  await pres.writeFile({ fileName });
};

export default downloadPropertyMonthlyReviewPowerpoint;
